import { Injectable, inject, effect } from '@angular/core';
import { OneSignal, IInitObject } from 'onesignal-ngx';
import { debugLog } from 'app/shared/utils/debug-logger';
import { environment } from '../../../environments/environment';
import { UserStore } from '../services/user.store';
import { LinkedUser } from 'app/api/models';

@Injectable({
  providedIn: 'root',
})
export class OneSignalService {
  private isOneSignalInitialized = false;
  private isUserInitialized = false;
  private userStore = inject(UserStore);
  private oneSignal = inject(OneSignal);

  constructor() {
    debugLog('OneSignal', 'Service constructor - OneSignal enabled:', environment.oneSignalConfig.enabled, 'AppId:', environment.oneSignalConfig.appId);
    if (environment.oneSignalConfig.enabled) {
      this.setupUserMonitoring();
      this.initializeService();
    }
  }

  /**
   * Initialize the service - only initializes OneSignal SDK, user setup happens separately
   */
  private async initializeService(): Promise<void> {
    try {
      debugLog('OneSignal', 'Starting service initialization');
      await this.init();
      debugLog('OneSignal', 'Service initialization completed');
    } catch (error) {
      console.error('[OneSignal] Failed to initialize OneSignal service:', error);
    }
  }

  /**
   * Initialize OneSignal with the appId from the environment
   */
  public async init(): Promise<void> {
    if (this.isOneSignalInitialized) {
      debugLog('OneSignal', 'Already initialized, skipping init');
      return;
    }

    try {
      debugLog('OneSignal', 'Starting OneSignal SDK initialization with appId:', environment.oneSignalConfig.appId);

      if (!environment.oneSignalConfig.appId) {
        throw new Error('OneSignal App ID is niet gedefinieerd in de omgeving');
      }

      // Check for existing subscription with different appId and unsubscribe if needed
      await this.handleExistingSubscription();

      const initOptions: IInitObject = {
        appId: environment.oneSignalConfig.appId,
        allowLocalhostAsSecureOrigin: false,
        safari_web_id: 'web.onesignal.auto.13169687-b109-4155-bbf4-37fb1a1576f8',
        serviceWorkerParam: {
          scope: '/onesignal/',
        },
        serviceWorkerPath: 'onesignal/OneSignalSDKWorker.js',
        notifyButton: {
          enable: true,
          displayPredicate: () => !this.oneSignal.Notifications.permission,
          position: 'bottom-left',
          size: 'medium',
          showCredit: false,
          text: {
            'dialog.blocked.message': 'Push notificaties zijn geblokkeerd',
            'dialog.blocked.title': 'Notificaties deblokkeren',
            'dialog.main.button.subscribe': 'Abonneren',
            'dialog.main.button.unsubscribe': 'Uitschrijven',
            'dialog.main.title': 'Push notificaties beheren',
            'message.action.resubscribed': 'Je bent nu geabonneerd',
            'message.action.subscribed': 'Bedankt voor het abonneren!',
            'message.action.subscribing': 'Abonneren...',
            'message.action.unsubscribed': 'Je bent nu uitgeschreven',
            'message.prenotify': 'Klik om je te abonneren op notificaties',
            'tip.state.blocked': 'Notificaties zijn geblokkeerd',
            'tip.state.subscribed': 'Je bent geabonneerd',
            'tip.state.unsubscribed': 'Abonneren op notificaties',
          },
          prenotify: true,
        },
      };

      await this.oneSignal.init(initOptions);
      this.isOneSignalInitialized = true;
      debugLog('OneSignal', 'SDK initialized successfully');
    } catch (error) {
      console.error('[OneSignal] Fout bij initialiseren van OneSignal:', error);
      throw error;
    }
  }

  /**
   * Handle existing subscription that might conflict with new appId
   */
  private async handleExistingSubscription(): Promise<void> {
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.getRegistration('/onesignal/');
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            debugLog('OneSignal', 'Found existing push subscription, unsubscribing to prevent conflicts');
            await subscription.unsubscribe();
            debugLog('OneSignal', 'Successfully unsubscribed existing subscription');
          }
        }
      }
    } catch (error) {
      console.warn('[OneSignal] Could not handle existing subscription:', error);
      // Don't throw - this is not critical for initialization
    }
  }

  /**
   * Monitor user state changes and initialize OneSignal for user when both user and OneSignal are ready
   */
  private setupUserMonitoring(): void {
    effect(() => {
      const loggedIn = this.userStore.loggedin();
      const users = this.userStore.users();
      const currentUser = this.userStore.currentUser();

      debugLog('OneSignal', 'User state changed:', {
        loggedIn,
        hasUsers: users && users.length > 0,
        hasCurrentUser: !!currentUser,
        currentUserId: currentUser?.userId,
        currentUserName: currentUser?.name,
        oneSignalInitialized: this.isOneSignalInitialized,
        userInitialized: this.isUserInitialized,
      });

      if (loggedIn && users && users.length > 0 && currentUser) {
        // Only proceed if OneSignal is initialized and user is not yet initialized
        if (this.isOneSignalInitialized && !this.isUserInitialized) {
          debugLog('OneSignal', 'Both OneSignal and user are ready, initializing user');
          this.initializeForUser(currentUser).catch(error => {
            console.error('[OneSignal] Failed to initialize OneSignal for user:', error);
          });
        } else if (!this.isOneSignalInitialized) {
          debugLog('OneSignal', 'User ready but OneSignal not yet initialized, waiting...');
        } else if (this.isUserInitialized) {
          debugLog('OneSignal', 'User already initialized, skipping');
        }
      } else if (!loggedIn) {
        debugLog('OneSignal', 'User logged out, performing OneSignal logout');
        this.logout().catch(error => {
          console.error('[OneSignal] Failed to logout from OneSignal:', error);
        });
      } else {
        debugLog('OneSignal', 'Waiting for user login and data to be available');
      }
    });
  }

  /**
   * Register and initialize OneSignal for a specific user
   */
  public async initializeForUser(user: LinkedUser): Promise<void> {
    debugLog('OneSignal', 'Starting user initialization for:', {
      userId: user.userId,
      userName: user.name,
      oneSignalInitialized: this.isOneSignalInitialized,
      userInitialized: this.isUserInitialized,
    });

    if (!this.isOneSignalInitialized) {
      console.error('[OneSignal] Cannot initialize user - OneSignal SDK not initialized yet');
      return;
    }

    if (this.isUserInitialized) {
      debugLog('OneSignal', 'User already initialized, skipping');
      return;
    }

    try {
      if (!user.userId || !user.name) {
        throw new Error('User must have userId and name');
      }

      debugLog('OneSignal', 'Logging in user with external ID:', user.userId);
      await this.oneSignal.login(user.userId);

      const emailAddress = this.userStore.emailAddress();
      debugLog('OneSignal', 'Adding user data - email:', emailAddress ? 'present' : 'not available');

      if (emailAddress) {
        this.oneSignal.User.addEmail(emailAddress);
        debugLog('OneSignal', 'Email added successfully');
      }

      debugLog('OneSignal', 'Adding aliases - name:', user.name, 'userId:', user.userId);
      this.oneSignal.User.addAlias('name', user.name);
      this.oneSignal.User.addAlias('userId', user.userId);

      const properties = {
        roles: user.userRoles || [],
        permissions: user.permissions || [],
      };

      debugLog('OneSignal', 'Adding user tags:', properties);
      this.oneSignal.User.addTags({
        user_properties: JSON.stringify(properties),
        user_id: user.userId,
      });

      // Request notification permission
      debugLog('OneSignal', 'Requesting notification permission');
      await this.oneSignal.Notifications.requestPermission();

      this.isUserInitialized = true;
      debugLog('OneSignal', 'User initialization completed successfully');
    } catch (error) {
      console.error('[OneSignal] Fout bij instellen OneSignal voor gebruiker:', error);
      throw error;
    }
  }

  /**
   * Log the user out of OneSignal
   */
  public async logout(): Promise<void> {
    debugLog('OneSignal', 'Logout requested - userInitialized:', this.isUserInitialized);

    if (!this.isUserInitialized) {
      debugLog('OneSignal', 'User not initialized, skipping logout');
      return;
    }

    try {
      debugLog('OneSignal', 'Performing OneSignal logout');
      await this.oneSignal.logout();
      this.isUserInitialized = false;
      debugLog('OneSignal', 'Logout completed successfully');
    } catch (error) {
      console.error('[OneSignal] Fout bij uitloggen van OneSignal:', error);
      throw error;
    }
  }
}
